import { useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// Shared look for every field in the announcement / poll window.
export const FIELD = "flex h-11 w-full items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 text-left text-sm text-white outline-none transition-colors duration-150 hover:border-white/20 focus-visible:border-[#00FF66]/60 data-[state=open]:border-[#00FF66]/60 data-[state=open]:bg-white/[0.05]";
export const TEXT_INPUT = "w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 text-sm text-white outline-none transition-colors duration-150 placeholder:text-white/30 hover:border-white/20 focus:border-[#00FF66]/60 focus:bg-white/[0.05]";
export const MENU = "z-[90] rounded-2xl border-white/10 bg-[#0d0f12] text-white shadow-[0_30px_80px_-20px_rgba(0,0,0,0.9)]";

export function FieldLabel({ children, hint }) {
  return (
    <div className="mb-2 flex items-baseline justify-between gap-3">
      <span className="text-[11px] font-semibold text-white/70">{children}</span>
      {hint && <span className="text-[10px] text-white/30">{hint}</span>}
    </div>
  );
}

export function Section({ title, children }) {
  return (
    <section className="space-y-4">
      <p className="flex items-center gap-3 text-[10px] font-bold uppercase tracking-[0.2em] text-white/35">
        {title}<span className="h-px flex-1 bg-white/[0.07]" />
      </p>
      {children}
    </section>
  );
}

export const Chevron = () => <ChevronDown className="h-4 w-4 shrink-0 text-white/35" />;

// Styled single-select that matches the date fields (replaces the native <select>).
export function SelectField({ value, onChange, options, icon: Icon, testid }) {
  const [open, setOpen] = useState(false);
  const current = options.find((o) => o.value === value) || options[0];
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={FIELD} data-testid={testid}>
          {Icon && <Icon className="h-4 w-4 shrink-0 text-[#00FF66]" />}
          <span className="flex-1 truncate font-semibold">{current?.label}</span>
          <Chevron />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={8} className={`${MENU} w-[var(--radix-popover-trigger-width)] p-1.5`}>
        <div className="max-h-64 overflow-y-auto">
          {options.map((o) => (
            <button key={o.value} type="button" onClick={() => { onChange(o.value); setOpen(false); }}
              className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm transition-colors duration-150 ${o.value === value ? "bg-[#00FF66]/10 text-white" : "text-white/70 hover:bg-white/[0.05] hover:text-white"}`}
              data-testid={`${testid}-option-${o.value || "all"}`}>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-semibold">{o.label}</span>
                {o.sub && <span className="block truncate text-[11px] text-white/35">{o.sub}</span>}
              </span>
              {o.value === value && <Check className="h-4 w-4 text-[#00FF66]" />}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
